import { usePresence } from '../lib/usePresence';
import { RoleBadge } from './RoleBadge';
import type { Role } from '../lib/api';
import { cn } from '../lib/cn';

// ─────────────────────────────────────────────────────────────────────────────
// PresenceBar — the "who else is on this page" face-pile. Quiet, on-brand
// (existing iron/ink tokens, no glow/gradient/severity strips), lives in a
// page header next to the title. Renders nothing when nobody else is here —
// this is ambient awareness, not a UI element that should ever compete for
// attention.
//
// Shows OTHERS only (self is excluded server-side, see presence.ts). Small
// initial chips give the compact "face-pile" read at a glance; the
// name + RoleBadge row (same RoleBadge used across the rest of the app,
// e.g. Sidebar/OrgPanel) expands on wider screens so it's still just a
// quiet label, never a wall of avatars.
// ─────────────────────────────────────────────────────────────────────────────

const MAX_VISIBLE = 4;

function initialsFor(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function PresenceBar({ resourceKey, className }: { resourceKey: string | undefined; className?: string }) {
  const { others } = usePresence(resourceKey);

  if (others.length === 0) return null;

  const visible = others.slice(0, MAX_VISIBLE);
  const overflow = others.length - visible.length;

  return (
    <div
      className={cn('flex items-center gap-2', className)}
      aria-label={`Also viewing this page: ${others.map(o => o.displayName).join(', ')}`}
    >
      <div className="flex items-center -space-x-1.5">
        {visible.map(v => (
          <span
            key={v.discordId}
            title={`${v.displayName} · ${v.role}`}
            className="flex h-6 w-6 items-center justify-center rounded-full border border-iron-950 bg-white/[0.08] font-display text-[10px] font-black uppercase tracking-wide text-ink-primary"
          >
            {initialsFor(v.displayName)}
          </span>
        ))}
        {overflow > 0 && (
          <span className="flex h-6 w-6 items-center justify-center rounded-full border border-iron-950 bg-white/[0.04] text-[9px] font-black text-ink-meta">
            +{overflow}
          </span>
        )}
      </div>

      <div className="hidden items-center gap-2 md:flex">
        <span className="text-[11px] text-ink-meta">Also here:</span>
        <div className="flex flex-wrap items-center gap-2">
          {visible.map(v => (
            <span key={v.discordId} className="inline-flex items-center gap-1.5">
              <span className="text-xs text-ink-secondary">{v.displayName}</span>
              <RoleBadge role={v.role as Role} size="xs" />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
